import type { ContextType } from 'react';

import type { ReactEditorViewContext } from '@atlaskit/editor-common/ui-react';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';

// Sourced from the editor-view context type so this file doesn't import the prosemirror EditorView
// re-export directly.
type EditorView = NonNullable<ContextType<typeof ReactEditorViewContext>['editorView']>;

/**
 * Position of the `mediaSingle` wrapping the media node with this id (media is its first child, so
 * the wrapper sits one position earlier), or the media node's own position when it isn't wrapped.
 */
const findMediaNodePosByMediaId = (doc: PMNode, mediaId: string): number | undefined => {
	let mediaPos: number | undefined;

	doc.descendants((node, pos, parent) => {
		if (mediaPos !== undefined) {
			return false;
		}
		if (node.type.name === 'media' && node.attrs.id === mediaId) {
			mediaPos = parent?.type.name === 'mediaSingle' ? pos - 1 : pos;
			return false;
		}
		return true;
	});

	return mediaPos;
};

/**
 * DOM element of the media node with this id, or null when it isn't in the document (yet). Shared
 * by the ready-signal hook and the viewport-placement hook so the ready bar's visibility and its
 * "View" action agree on which element represents the result.
 */
export const resolveRemixImageElement = (
	editorView: EditorView,
	mediaId: string,
): HTMLElement | null => {
	const mediaPos = findMediaNodePosByMediaId(editorView.state.doc, mediaId);
	const mediaDom = mediaPos !== undefined ? editorView.nodeDOM(mediaPos) : null;
	return mediaDom instanceof HTMLElement ? mediaDom : (mediaDom?.parentElement ?? null);
};
